import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Typography, Select, Option, Button, IconButton, Tooltip } from "@material-tailwind/react";
import { Link, useNavigate } from 'react-router-dom';
import { FaInfoCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import { rootUrl } from '../../../../config/config';
import DriverTypeSummaryPivot from './summary/DriverTypeSummaryPivot';
import TypeSummaryTable from './summary/TypeSummaryTable';
import ConfirmModal from '../../../common/ConfirmModal';
import DriverTransactionRowActions from './DriverTransactionRowActions';

const DriverTransactionsList = () => {
    const navigate = useNavigate();
    const authToken = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const getHeaders = () => authToken ? { Authorization: `Bearer ${authToken}` } : {};

    const [transactions, setTransactions] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [transTypes, setTransTypes] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [selectedDeleteTransaction, setSelectedDeleteTransaction] = useState(null);
    const canDeleteTransactions = permissions.includes('roles.manage') || permissions.includes('drivers.delete');

    // Initialize filters with current week (Monday to Today)
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(today);
        monday.setDate(diff);

        return {
            driver_id: '',
            type: '',
            week: '',
            date_from: monday.toISOString().split('T')[0],
            date_to: new Date().toISOString().split('T')[0]
        };
    });

    const [summary, setSummary] = useState({
        totalDebit: 0,
        totalCredit: 0,
        balance: 0
    });

    const [typeSummaries, setTypeSummaries] = useState({});
    const [driverPivot, setDriverPivot] = useState({ rows: [], columns: [], totals: {} });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState('');

    // Reset to page 1 when filters change or itemsPerPage changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, itemsPerPage]);

    // Format date to "Nov 23, 2025"
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const calculateSummary = (data) => {
        let debit = 0;
        let credit = 0;
        const typeSum = {};
        const driverMap = {};
        const typesSet = new Set();

        data.forEach(t => {
            const d = parseFloat(t.debit || 0);
            const c = parseFloat(t.credit || 0);
            debit += d;
            credit += c;

            // Type Summary
            const typeName = t.short_desc || t.type || 'Unknown';
            if (!typeSum[typeName]) {
                typeSum[typeName] = { 
                    debit: 0, 
                    credit: 0, 
                    net: 0,
                    type: t.type,
                    sub_type: t.sub_type
                };
            }
            typeSum[typeName].debit += d;
            typeSum[typeName].credit += c;
            typeSum[typeName].net += (d - c);

            // Driver Pivot Data - Group by sub_type instead of type name
            const driverId = t.driver_id;
            const driverName = t.driver_name || t.driver_id || 'Unknown';
            const subType = t.sub_type || 'Other';

            if (!driverMap[driverId]) {
                driverMap[driverId] = {
                    id: driverId,
                    name: driverName,
                    types: {},
                    total: 0
                };
            }

            if (!driverMap[driverId].types[subType]) driverMap[driverId].types[subType] = 0;
            driverMap[driverId].types[subType] += (d - c);
            driverMap[driverId].total += (d - c);

            typesSet.add(subType);
        });

        setSummary({
            totalDebit: debit,
            totalCredit: credit,
            balance: debit - credit
        });
        setTypeSummaries(typeSum);

        const sortedTypes = Array.from(typesSet).sort();
        const sortedDrivers = Object.values(driverMap).sort((a, b) => a.name.localeCompare(b.name));

        // Calculate column totals for the pivot
        const colTotals = {};
        sortedTypes.forEach(type => {
            colTotals[type] = sortedDrivers.reduce((sum, d) => sum + (d.types[type] || 0), 0);
        });

        setDriverPivot({
            rows: sortedDrivers,
            columns: sortedTypes,
            totals: colTotals
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            let query = `${rootUrl}/drivers/transactions?limit=10000`;
            if (filters.driver_id) query += `&driver_id=${filters.driver_id}`;
            if (filters.type) query += `&type=${filters.type}`;
            if (filters.week) query += `&week=${filters.week}`;
            if (filters.date_from) query += `&date_from=${filters.date_from}`;
            if (filters.date_to) query += `&date_to=${filters.date_to}`;

            const res = await axios.get(query, {
                headers: getHeaders()
            });

            if (res.data.success) {
                setTransactions(res.data.data);
                calculateSummary(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, authToken]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const fetchDrivers = async () => {
            try {
                const res = await axios.get(`${rootUrl}/drivers?limit=1000`, {
                    headers: getHeaders()
                });
                setDrivers(res.data.data || []);
            } catch (error) {
                console.error('Error fetching drivers:', error);
            }
        };

        const fetchTransTypes = async () => {
            try {
                const res = await axios.get(`${rootUrl}/payments/driver-types`, {
                    headers: getHeaders()
                });
                setTransTypes(res.data.data || []);
            } catch (error) {
                console.error('Error fetching transaction types:', error);
            }
        };

        const fetchWeeks = async () => {
            try {
                // Fetch system weeks from dedicated endpoint
                const res = await axios.get(`${rootUrl}/system/weeks`, {
                    headers: getHeaders()
                });
                if (res.data.success) {
                    setWeeks(res.data.data);
                }
            } catch (error) {
                console.error('Error fetching weeks:', error);
            }
        };

        fetchDrivers();
        fetchTransTypes();
        fetchWeeks();
        fetchTransactions();
    }, [fetchTransactions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        (async () => {
            if (!authToken) {
                setPermissions([]);
                return;
            }

            try {
                const res = await axios.get(`${rootUrl}/api/users/me/permissions`, { headers: getHeaders() });
                setPermissions(res.data?.data || []);
            } catch (error) {
                setPermissions([]);
            }
        })();
    }, [authToken]);

    const handleFilterChange = async (name, value) => {
        if (name === 'week' && value && value !== '') {
            // Find the selected week from the weeks array
            const selectedWeek = weeks.find(w => w.week === value);
            if (selectedWeek) {
                // Set the date range based on the week's start and end dates
                setFilters(prev => ({
                    ...prev,
                    week: value,
                    date_from: new Date(selectedWeek.start_date).toISOString().split('T')[0],
                    date_to: new Date(selectedWeek.end_date).toISOString().split('T')[0]
                }));
                return;
            }
        } else if (name === 'week' && (!value || value === '')) {
            // Reset to current week dates when "All Weeks" is selected
            const today = new Date();
            const day = today.getDay();
            const diff = today.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(today);
            monday.setDate(diff);
            
            setFilters(prev => ({
                ...prev,
                week: '',
                date_from: monday.toISOString().split('T')[0],
                date_to: new Date().toISOString().split('T')[0]
            }));
            return;
        }
        
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleViewTransaction = (transaction) => {
        navigate(`/drivers/transactions/${transaction.type}/${transaction.doc_no}`);
    };

    const handleDeleteRequest = (transaction) => {
        setDeleteError('');
        setSelectedDeleteTransaction(transaction);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedDeleteTransaction) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await axios.delete(
                `${rootUrl}/drivers/transactions/${encodeURIComponent(selectedDeleteTransaction.driver_id)}/${encodeURIComponent(selectedDeleteTransaction.type)}/${encodeURIComponent(selectedDeleteTransaction.doc_no)}`,
                {
                    headers: getHeaders(),
                    data: { reason: null }
                }
            );
            setDeleteModalOpen(false);
            setSelectedDeleteTransaction(null);
            await fetchTransactions();
        } catch (err) {
            setDeleteError(err.response?.data?.message || err.message || 'Failed to delete transaction');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] min-h-screen p-6">
            {/* Header Section */}
            <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Driver Transactions</h1>
                <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                <p className="text-lg text-[#2a2e30] opacity-80">
                    Manage and track all driver financial transactions
                </p>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">
                {/* Filters & Transaction List */}
                <Card className="bg-white rounded-2xl shadow-xl p-6">
                    <CardBody>
                        <div className="flex flex-wrap gap-4 mb-6 items-end">
                            <div className="w-full sm:w-56">
                                <Select
                                    label="Driver"
                                    value={filters.driver_id}
                                    onChange={(val) => handleFilterChange('driver_id', val)}
                                >
                                    <Option value="">All Drivers</Option>
                                    {drivers.map(d => (
                                        <Option key={d.id} value={d.id_no}>{d.driver_name}</Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="w-full sm:w-56">
                                <Select
                                    label="Type"
                                    value={filters.type}
                                    onChange={(val) => handleFilterChange('type', val)}
                                >
                                    <Option value="">All Types</Option>
                                    {transTypes.map(t => (
                                        <Option key={t.type} value={t.type}>{t.short_desc}</Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="w-full sm:w-48">
                                <Select
                                    label="Week"
                                    value={filters.week}
                                    onChange={(val) => handleFilterChange('week', val)}
                                >
                                    <Option value="">All Weeks</Option>
                                    {weeks.map(w => (
                                        <Option key={w.id} value={w.week}>
                                            {w.week} ({new Date(w.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(w.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="w-full sm:w-44">
                                <label className="block text-sm font-medium text-blue-gray-700 mb-1">From</label>
                                <input
                                    type="date"
                                    className={`w-full px-3 py-2 border border-blue-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${filters.week ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                    disabled={!!filters.week}
                                />
                            </div>
                            <div className="w-full sm:w-44">
                                <label className="block text-sm font-medium text-blue-gray-700 mb-1">To</label>
                                <input
                                    type="date"
                                    className={`w-full px-3 py-2 border border-blue-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${filters.week ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    value={filters.date_to}
                                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                    disabled={!!filters.week}
                                />
                            </div>
                            <Button
                                color="blue"
                                onClick={fetchTransactions}
                                className="w-full sm:w-auto"
                            >
                                Filter
                            </Button>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full min-w-max table-auto text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-blue-gray-200">
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Date</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Week</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Doc No</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Ref</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Driver</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Car</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Type</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm text-right">Debit</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm text-right">Credit</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Notes</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="11" className="p-4 text-center">Loading...</td></tr>
                                    ) : transactions.length === 0 ? (
                                        <tr><td colSpan="11" className="p-4 text-center text-gray-500">No transactions found</td></tr>
                                    ) : (
                                        <>
                                            {transactions
                                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                                .map((t) => (
                                                <tr key={t.id} className="border-b border-blue-gray-100 hover:bg-blue-gray-50 transition-colors">
                                                    <td className="p-3 text-sm">{formatDate(t.date)}</td>
                                                    <td className="p-3 text-sm">{t.week || '-'}</td>
                                                    <td className="p-3 text-sm">
                                                        <span className="font-medium text-slate-700">{t.doc_no}</span>
                                                    </td>
                                                    <td className="p-3 text-sm text-gray-600">{t.your_ref || t.our_ref || '-'}</td>
                                                    <td className="p-3 text-sm">
                                                        <Link to={`/drivers/${t.driver_id}`} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
                                                            {t.driver_name || t.driver_id}
                                                        </Link>
                                                    </td>
                                                    <td className="p-3 text-sm">
                                                        {t.plate_no ? (
                                                            <Link to={`/cars/${t.plate_no}`} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
                                                                {t.plate_no}
                                                            </Link>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="p-3 text-sm">
                                                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                                            {t.short_desc}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-sm text-right font-semibold text-red-600">
                                                        {parseFloat(t.debit) > 0 ? parseFloat(t.debit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                    </td>
                                                    <td className="p-3 text-sm text-right font-semibold text-green-600">
                                                        {parseFloat(t.credit) > 0 ? parseFloat(t.credit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                    </td>
                                                    <td className="p-3 text-sm">
                                                        {t.notes ? (
                                                            <Tooltip content={t.notes} placement="left">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedNote(t.notes);
                                                                        setShowNoteModal(true);
                                                                    }}
                                                                    className="text-blue-500 hover:text-blue-700"
                                                                >
                                                                    <FaInfoCircle />
                                                                </button>
                                                            </Tooltip>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="p-3 text-sm">
                                                        <DriverTransactionRowActions
                                                            transaction={t}
                                                            canDelete={canDeleteTransactions && !t.parent_trans_id}
                                                            onView={handleViewTransaction}
                                                            onDelete={handleDeleteRequest}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            {/* Totals Row */}
                                            <tr className="bg-blue-gray-50 font-bold border-t-2 border-blue-gray-300">
                                                <td colSpan="7" className="p-3 text-right text-sm">TOTALS:</td>
                                                <td className="p-3 text-sm text-right text-red-700">
                                                    {summary.totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                                <td className="p-3 text-sm text-right text-green-700">
                                                    {summary.totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                                <td className="p-3"></td>
                                                <td className="p-3"></td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {transactions.length > 0 && (
                            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-blue-gray-200 gap-4">
                                <div className="flex items-center gap-4">
                                    <Typography variant="small" color="blue-gray">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, transactions.length)} of {transactions.length} transactions
                                    </Typography>
                                    <div className="flex items-center gap-2">
                                        <Typography variant="small" color="blue-gray">
                                            Per page:
                                        </Typography>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                            }}
                                            className="px-3 py-1.5 border border-blue-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <IconButton
                                        size="sm"
                                        variant="outlined"
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <FaChevronLeft className="h-4 w-4" />
                                    </IconButton>
                                    <Typography variant="small" color="blue-gray" className="flex items-center px-4">
                                        Page {currentPage} of {Math.max(1, Math.ceil(transactions.length / itemsPerPage))}
                                    </Typography>
                                    <IconButton
                                        size="sm"
                                        variant="outlined"
                                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(transactions.length / itemsPerPage), prev + 1))}
                                        disabled={currentPage >= Math.ceil(transactions.length / itemsPerPage)}
                                    >
                                        <FaChevronRight className="h-4 w-4" />
                                    </IconButton>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Type Summary Table */}
                <TypeSummaryTable typeSummaries={typeSummaries} summary={summary} />

                {/* Driver x Type Summary Pivot */}
                <DriverTypeSummaryPivot driverPivot={driverPivot} balance={summary.balance} />

                {/* Note Modal */}
                {showNoteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowNoteModal(false)}>
                        <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4">
                                <Typography variant="h5" color="blue-gray">Transaction Note</Typography>
                                <button
                                    onClick={() => setShowNoteModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                                <Typography variant="paragraph" color="blue-gray" className="whitespace-pre-wrap">
                                    {selectedNote}
                                </Typography>
                            </div>
                            <div className="mt-6 flex justify-end">
                                <Button color="blue" onClick={() => setShowNoteModal(false)}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <ConfirmModal
                    isOpen={deleteModalOpen}
                    title="Delete Transaction"
                    description={selectedDeleteTransaction ? `Delete transaction ${selectedDeleteTransaction.doc_no} for driver ${selectedDeleteTransaction.driver_id}? This will archive it.` : ''}
                    onCancel={() => {
                        if (deleteLoading) return;
                        setDeleteModalOpen(false);
                        setSelectedDeleteTransaction(null);
                        setDeleteError('');
                    }}
                    onConfirm={handleDeleteConfirm}
                    confirmText="Delete"
                    cancelText="Cancel"
                    loading={deleteLoading}
                    error={deleteError}
                    variant="danger"
                />
            </div>
        </div>
    );
};

export default DriverTransactionsList;
